// src/components/ModelMultiSelect.tsx
import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import type { Model } from '../pages/Rotate'; // o donde tengas el type

interface Props {
  models: Model[];
  value: string[];
  onChange: (values: string[]) => void;
}

const ModelMultiSelect: React.FC<Props> = ({ models, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggleValue = (val: string) => {
    if (val === 'ALL') {
      onChange(['ALL']);
      return;
    }
    const current = value.includes('ALL') ? [] : value;
    const exists = current.includes(val);
    const next = exists ? current.filter(v => v !== val) : [...current, val];
    onChange(next.length === 0 ? ['ALL'] : next);
  };

  const labelFor = (val: string) =>
    models.find(m => m.value === val)?.label ?? val;

  const visibleLabels =
    value.includes('ALL') ? ['ALL'] : value.map(labelFor);

  return (
    <div className="model-multiselect" ref={ref}>
      <button
        type="button"
        className="model-multiselect-trigger"
        onClick={() => setOpen(o => !o)}
      >
        <div className="model-multiselect-chips">
          {visibleLabels.length === 0 ? (
            <span className="model-multiselect-placeholder">Select models…</span>
          ) : (
            visibleLabels.map(l => (
              <span key={l} className="model-chip">
                {l}
              </span>
            ))
          )}
        </div>
        <ChevronDown className="model-multiselect-icon" size={16} />
      </button>

      {open && (
        <div className="model-multiselect-dropdown">
          <button
            type="button"
            className={`model-option ${
              value.includes('ALL') ? 'model-option--selected' : ''
            }`}
            onClick={() => toggleValue('ALL')}
          >
            <span>ALL (run all models)</span>
            {value.includes('ALL') && <Check size={14} />}
          </button>
          <div className="model-options-separator" />
          {models
            .filter(m => m.value !== 'ALL')
            .map(({ value: v, label }) => {
              const selected =
                value.includes('ALL') ? false : value.includes(v);
              return (
                <button
                  key={v}
                  type="button"
                  className={`model-option ${
                    selected ? 'model-option--selected' : ''
                  }`}
                  onClick={() => toggleValue(v)}
                >
                  <span>{label}</span>
                  {selected && <Check size={14} />}
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
};

export default ModelMultiSelect;
